import React, { useEffect, useState } from 'react';
import 'bootstrap/dist/css/bootstrap.min.css';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import { Modal, Button, Form } from 'react-bootstrap';

function AlbumList() {
    const [albums, setAlbums] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subcategories, setSubcategories] = useState([]);

    const [searchTerm, setSearchTerm] = useState('');
    const [currentPage] = useState(1);
    const [itemsPerPage] = useState(100);

    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);

    const [albumId, setAlbumId] = useState(null);

    const [categoryId, setCategoryId] = useState('');
    const [subcategoryId, setSubcategoryId] = useState('');

    const [albumImages, setAlbumImages] = useState([]);
    const [imagePreview, setImagePreview] = useState([]);

    const [loadingSubcategories, setLoadingSubcategories] = useState(false);

    // =========================================================
    // FETCH ALBUMS
    // =========================================================

    const fetchAlbums = async () => {
        try {
            const token = localStorage.getItem('authToken');

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/albums`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = await res.json();

            setAlbums(data.albums || []);

        } catch (error) {
            console.error('Failed to fetch albums:', error);
        }
    };

    // =========================================================
    // FETCH CATEGORIES
    // =========================================================

    const fetchCategories = async () => {
        try {
            const token = localStorage.getItem('authToken');

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/getcategories`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = await res.json();

            setCategories(data.data || []);

        } catch (error) {
            console.error('Failed to fetch categories:', error);
        }
    };

    // =========================================================
    // FETCH SUBCATEGORIES BY CATEGORY
    // =========================================================

    const fetchSubcategories = async (selectedCategoryId) => {

        if (!selectedCategoryId) {
            setSubcategories([]);
            setSubcategoryId('');
            return;
        }

        try {

            setLoadingSubcategories(true);

            const token = localStorage.getItem('authToken');

            const res = await fetch(
                `${process.env.REACT_APP_API_URL}/getsubcategory/category/${selectedCategoryId}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            if (res.status === 404) {
                setSubcategories([]);
                setSubcategoryId('');
                return;
            }

            const data = await res.json();

            setSubcategories(data.data || []);

        } catch (error) {

            console.error(
                'Failed to fetch subcategories:',
                error
            );

            setSubcategories([]);

        } finally {

            setLoadingSubcategories(false);

        }
    };

    // =========================================================
    // INITIAL LOAD
    // =========================================================

    useEffect(() => {

        fetchAlbums();
        fetchCategories();

    }, []);

    // =========================================================
    // WHEN CATEGORY CHANGES
    // =========================================================

    useEffect(() => {

        if (categoryId) {
            fetchSubcategories(categoryId);
        } else {
            setSubcategories([]);
            setSubcategoryId('');
        }

    }, [categoryId]);

    // =========================================================
    // SEARCH
    // =========================================================

    const filteredAlbums = albums.filter((item) =>
        Object.values(item).some((val) =>
            String(val || '')
                .toLowerCase()
                .includes(searchTerm.toLowerCase())
        )
    );

    const currentItems = filteredAlbums.slice(
        (currentPage - 1) * itemsPerPage,
        currentPage * itemsPerPage
    );

    // =========================================================
    // ADD / UPDATE ALBUM
    // =========================================================

    const handleSubmit = async () => {

        try {

            if (!categoryId) {
                alert('Please select a category');
                return;
            }

            if (!isEditing && albumImages.length === 0) {
                alert('Please select at least one image');
                return;
            }

            const token = localStorage.getItem('authToken');

            const formData = new FormData();

            // REQUIRED CATEGORY
            formData.append(
                'category_id',
                categoryId
            );

            // OPTIONAL SUBCATEGORY
            // If no subcategory is selected, send empty string.
            // Backend converts this to NULL.
            formData.append(
                'subcategory_id',
                subcategoryId || ''
            );

            // =================================================
            // UPDATE
            // =================================================

            if (isEditing) {

                // Update accepts only one image
                if (albumImages.length > 0) {

                    formData.append(
                        'album_file',
                        albumImages[0]
                    );

                }

            }

            // =================================================
            // CREATE
            // =================================================

            else {

                albumImages.forEach((file) => {

                    formData.append(
                        'album_file',
                        file
                    );

                });

            }

            const url = isEditing
                ? `${process.env.REACT_APP_API_URL}/updatealbums/${albumId}`
                : `${process.env.REACT_APP_API_URL}/addalbums`;

            const method = isEditing
                ? 'PUT'
                : 'POST';

            const response = await fetch(
                url,
                {
                    method,
                    headers: {
                        Authorization: `Bearer ${token}`
                    },
                    body: formData
                }
            );

            const data = await response.json();

            if (!response.ok) {

                console.error(
                    'Album API error:',
                    data
                );

                alert(
                    data.error ||
                    data.message ||
                    'Failed to save album'
                );

                return;
            }

            alert(
                isEditing
                    ? 'Album updated successfully'
                    : 'Album images added successfully'
            );

            await fetchAlbums();

            setShowModal(false);

            resetForm();

        } catch (error) {

            console.error(
                'Failed to save album:',
                error
            );

            alert(
                'Failed to save album'
            );

        }
    };

    // =========================================================
    // EDIT ALBUM
    // =========================================================

    const handleEdit = async (item) => {

        setAlbumId(item.album_id);

        // Set category first
        setCategoryId(
            String(item.category_id)
        );

        // Existing subcategory
        setSubcategoryId(
            item.subcategory_id
                ? String(item.subcategory_id)
                : ''
        );

        setImagePreview([
            item.image_url
        ]);

        setAlbumImages([]);

        setIsEditing(true);

        // Load subcategories of this category
        await fetchSubcategories(
            item.category_id
        );

        setShowModal(true);
    };

    // =========================================================
    // DELETE ALBUM
    // =========================================================

    const handleDelete = async () => {

        try {

            const token =
                localStorage.getItem('authToken');

            const response = await fetch(
                `${process.env.REACT_APP_API_URL}/albums/${albumId}`,
                {
                    method: 'DELETE',
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                }
            );

            const data = await response.json();

            if (!response.ok) {

                alert(
                    data.error ||
                    data.message ||
                    'Failed to delete album'
                );

                return;
            }

            await fetchAlbums();

            setShowDeleteModal(false);

            setAlbumId(null);

        } catch (error) {

            console.error(
                'Failed to delete album:',
                error
            );

        }
    };

    // =========================================================
    // RESET FORM
    // =========================================================

    const resetForm = () => {

        setAlbumId(null);

        setCategoryId('');

        setSubcategoryId('');

        setSubcategories([]);

        setAlbumImages([]);

        setImagePreview([]);

        setIsEditing(false);

    };

    // =========================================================
    // IMAGE CHANGE
    // =========================================================

    const handleImageChange = (e) => {

        const files = Array.from(
            e.target.files
        );

        setAlbumImages(files);

        const previews = files.map(
            (file) =>
                URL.createObjectURL(file)
        );

        setImagePreview(previews);
    };

    // =========================================================
    // CATEGORY CHANGE
    // =========================================================

    const handleCategoryChange = (e) => {

        const selectedCategoryId =
            e.target.value;

        setCategoryId(
            selectedCategoryId
        );

        // Important:
        // When category changes,
        // old subcategory must be removed.
        setSubcategoryId('');

        setSubcategories([]);

        if (selectedCategoryId) {

            fetchSubcategories(
                selectedCategoryId
            );

        }
    };

    // =========================================================
    // OPEN ADD MODAL
    // =========================================================

    const handleAddAlbum = () => {

        resetForm();

        setShowModal(true);

    };

    // =========================================================
    // UI
    // =========================================================

    return (
        <div
            className="container"
            style={{
                marginTop: '80px'
            }}
        >

            <h2 className="text-primary">
                Album List
            </h2>

            {/* =================================================
                TOP BAR
            ================================================= */}

            <div className="d-flex justify-content-between mb-3">

                <input
                    className="form-control w-25"
                    placeholder="Search..."
                    value={searchTerm}
                    onChange={(e) =>
                        setSearchTerm(
                            e.target.value
                        )
                    }
                />

                <button
                    className="btn btn-success"
                    onClick={handleAddAlbum}
                >
                    <FaPlus /> Add Album
                </button>

            </div>

            {/* =================================================
                TABLE
            ================================================= */}

            <table className="table table-bordered">

                <thead>

                    <tr>

                        <th>ID</th>

                        <th>Category</th>

                        <th>Subcategory</th>

                        <th>Serial No</th>

                        <th>Image</th>

                        <th>Actions</th>

                    </tr>

                </thead>

                <tbody>

                    {currentItems.length === 0 ? (

                        <tr>

                            <td
                                colSpan="6"
                                className="text-center"
                            >
                                No albums found
                            </td>

                        </tr>

                    ) : (

                        currentItems.map(
                            (item) => (

                                <tr
                                    key={
                                        item.album_id
                                    }
                                >

                                    <td>
                                        {
                                            item.album_id
                                        }
                                    </td>

                                    <td>
                                        {
                                            item.category_name ||
                                            item.category ||
                                            '-'
                                        }
                                    </td>

                                    <td>
                                        {
                                            item.subcategory_name ||
                                            '-'
                                        }
                                    </td>

                                    <td>
                                        {
                                            item.serial_no
                                        }
                                    </td>

                                    <td>

                                        <img
                                            src={
                                                item.image_url
                                            }
                                            alt="Album"
                                            width="50"
                                            height="50"
                                            style={{
                                                objectFit:
                                                    'cover'
                                            }}
                                        />

                                    </td>

                                    <td>

                                        <button
                                            className="btn btn-sm btn-success me-2"
                                            onClick={() =>
                                                handleEdit(
                                                    item
                                                )
                                            }
                                        >
                                            <FaEdit />
                                        </button>

                                        <button
                                            className="btn btn-sm btn-danger"
                                            onClick={() => {

                                                setAlbumId(
                                                    item.album_id
                                                );

                                                setShowDeleteModal(
                                                    true
                                                );

                                            }}
                                        >
                                            <FaTrash />
                                        </button>

                                    </td>

                                </tr>

                            )
                        )

                    )}

                </tbody>

            </table>

            {/* =================================================
                ADD / EDIT MODAL
            ================================================= */}

            <Modal
                show={showModal}
                onHide={() => {

                    setShowModal(false);

                    resetForm();

                }}
            >

                <Modal.Header closeButton>

                    <Modal.Title>

                        {
                            isEditing
                                ? 'Edit Album'
                                : 'Add Album'
                        }

                    </Modal.Title>

                </Modal.Header>

                <Modal.Body>

                    <Form>

                        {/* ================================
                            CATEGORY
                        ================================= */}

                        <Form.Group className="mb-3">

                            <Form.Label>
                                Category
                            </Form.Label>

                            <Form.Select
                                value={categoryId}
                                onChange={
                                    handleCategoryChange
                                }
                            >

                                <option value="">
                                    Select Category
                                </option>

                                {categories.map(
                                    (cat) => (

                                        <option
                                            key={
                                                cat.category_id
                                            }
                                            value={
                                                cat.category_id
                                            }
                                        >
                                            {
                                                cat.category
                                            }
                                        </option>

                                    )
                                )}

                            </Form.Select>

                        </Form.Group>

                        {/* ================================
                            SUBCATEGORY
                        ================================= */}

                        <Form.Group className="mb-3">

                            <Form.Label>
                                Subcategory
                            </Form.Label>

                            <Form.Select
                                value={
                                    subcategoryId
                                }
                                onChange={(e) =>
                                    setSubcategoryId(
                                        e.target.value
                                    )
                                }
                                disabled={
                                    !categoryId ||
                                    loadingSubcategories
                                }
                            >

                                <option value="">

                                    {
                                        loadingSubcategories
                                            ? 'Loading subcategories...'
                                            : !categoryId
                                                ? 'Select category first'
                                                : subcategories.length === 0
                                                    ? 'No subcategory - save under category'
                                                    : 'Select Subcategory (Optional)'
                                    }

                                </option>

                                {subcategories.map(
                                    (sub) => (

                                        <option
                                            key={
                                                sub.subcategory_id
                                            }
                                            value={
                                                sub.subcategory_id
                                            }
                                        >
                                            {
                                                sub.subcategory
                                            }
                                        </option>

                                    )
                                )}

                            </Form.Select>

                            <small className="text-muted">

                                If you don't select a
                                subcategory, the album
                                image will be saved directly
                                under the selected category.

                            </small>

                        </Form.Group>

                        {/* ================================
                            IMAGE
                        ================================= */}

                        <Form.Group>

                            <Form.Label>
                                {
                                    isEditing
                                        ? 'Replace Image (Optional)'
                                        : 'Album Images'
                                }
                            </Form.Label>

                            <Form.Control
                                type="file"
                                multiple={!isEditing}
                                accept="image/*"
                                onChange={
                                    handleImageChange
                                }
                            />

                            <small className="text-muted">

                                {
                                    isEditing
                                        ? 'Select one image if you want to replace the current image.'
                                        : 'You can select multiple images.'
                                }

                            </small>

                        </Form.Group>

                        {/* ================================
                            IMAGE PREVIEW
                        ================================= */}

                        <div className="mt-3">

                            {imagePreview &&
                                imagePreview.map(
                                    (img, i) => (

                                        <img
                                            key={i}
                                            src={img}
                                            alt={
                                                `preview-${i}`
                                            }
                                            width="80"
                                            height="80"
                                            className="mt-2 me-2"
                                            style={{
                                                objectFit:
                                                    'cover',
                                                border:
                                                    '1px solid #ddd',
                                                borderRadius:
                                                    '5px'
                                            }}
                                        />

                                    )
                                )}

                        </div>

                    </Form>

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() => {

                            setShowModal(false);

                            resetForm();

                        }}
                    >
                        Close
                    </Button>

                    <Button
                        variant="primary"
                        onClick={
                            handleSubmit
                        }
                    >
                        {
                            isEditing
                                ? 'Update'
                                : 'Add'
                        }
                    </Button>

                </Modal.Footer>

            </Modal>

            {/* =================================================
                DELETE MODAL
            ================================================= */}

            <Modal
                show={showDeleteModal}
                onHide={() =>
                    setShowDeleteModal(
                        false
                    )
                }
            >

                <Modal.Body>

                    Are you sure you want to
                    delete this album image?

                </Modal.Body>

                <Modal.Footer>

                    <Button
                        variant="secondary"
                        onClick={() =>
                            setShowDeleteModal(
                                false
                            )
                        }
                    >
                        Cancel
                    </Button>

                    <Button
                        variant="danger"
                        onClick={
                            handleDelete
                        }
                    >
                        Delete
                    </Button>

                </Modal.Footer>

            </Modal>

        </div>
    );
}

export default AlbumList;